import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import clsx from "clsx";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import UseCaseBlock from "./components/UseCaseBlock";

interface UseCaseFilterProps {
    shouldConfirm?: boolean;
    small?: boolean;
}

export interface UseCaseFilterRef {
    confirmChange: () => void;
}

const UseCaseFilter = forwardRef<UseCaseFilterRef, UseCaseFilterProps>(({ shouldConfirm, small }, ref) => {
    const useCases = useDataStore((state) => state.useCases);
    const activeVersion = useFilterStore((state) => state.activeModalVersion);
    const activeUseCases = useFilterStore((state) => state.activeUseCases);
    const setActiveUseCases = useFilterStore((state) => state.setActiveUseCases);

    const availableRegions = useLocaleStore((state) => state.availableRegions);
    const activeRegion = useLocaleStore((state) => state.activeRegion);

    const [expanded, setExpanded] = useState<string | null>(null);

    const unconfirmedUseCases = useFilterStore((state) => state.unconfirmedUseCases);
    const setUnconfirmedUseCases = useFilterStore((state) => state.setUnconfirmedUseCases);

    const columns = useMemo(() => {
        if (!useCases[activeVersion!]) return [];

        let useCasesInRegion = useCases[activeVersion!];
        const availableRegionCodes = availableRegions.map((r) => r.regionCode.toLowerCase());
        if (availableRegions.length > 0) {
            useCasesInRegion = useCasesInRegion.filter((uc) => {
                const potentialRegionCode = uc.code.slice(-2).toLowerCase();
                if (availableRegionCodes.includes(potentialRegionCode)) {
                    return potentialRegionCode === activeRegion?.toLowerCase();
                }

                return true;
            });
        }

        const sorted = useCasesInRegion.sort((a, b) => a.code.localeCompare(b.code));
        return [sorted.filter((x) => sorted.indexOf(x) % 2 === 0), sorted.filter((x) => sorted.indexOf(x) % 2 !== 0)];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useCases, activeVersion]);

    useImperativeHandle(ref, () => ({
        confirmChange: () => {
            setActiveUseCases(unconfirmedUseCases);
        },
    }));

    return columns.map((column, index) => {
        return (
            <div key={index} className={clsx(["flex flex-col", small ? "gap-1" : "gap-1 xl:gap-4"])}>
                {column.map((useCase) => {
                    const id = `usecase-${shouldConfirm ? "confirm" : "direct"}-${useCase.id}`;
                    const isActive = activeUseCases.includes(useCase.id);
                    const isUnconfirmed = unconfirmedUseCases.includes(useCase.id);
                    const isSelected = shouldConfirm ? isUnconfirmed : isActive;
                    const isExpanded = expanded === useCase.id;

                    const handleSelect = () => {
                        if (shouldConfirm) {
                            const currentUseCases = isUnconfirmed
                                ? unconfirmedUseCases.filter((id) => id !== useCase.id)
                                : [...unconfirmedUseCases, useCase.id];

                            setUnconfirmedUseCases(currentUseCases);
                        } else {
                            const currentUseCases = isActive
                                ? activeUseCases.filter((id) => id !== useCase.id)
                                : [...activeUseCases, useCase.id];

                            setActiveUseCases(currentUseCases);
                        }
                    };

                    const handleClick = () => {
                        setExpanded((prev) => (prev === useCase.id ? null : useCase.id));
                    };

                    return (
                        <UseCaseBlock
                            key={id}
                            id={id}
                            useCase={useCase}
                            selected={isSelected}
                            expanded={isExpanded}
                            small={small}
                            onClick={handleClick}
                            onSelect={handleSelect}
                        />
                    );
                })}
            </div>
        );
    });
});

export default UseCaseFilter;
